/* eslint-disable max-lines */
import {
  WORKING_DAYS_WEEK_TYPE,
  getTimeRangesByGranularity
} from '../../../dashboard/util';
import {
  FILTER_BY,
  FORMATTERS,
  GRANULARITY,
  GRAPH_TYPES,
  METRICS_GOALS,
  METRICS_METADATA
} from './consts';
import {
  DASHBOARD_INTERNAL_TYPES,
  LOCAL_STORAGE_KEYS,
  SHORT_DATE_FORMAT
} from 'common/constants/constants';
import { getColor } from 'common/constants/_colors';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import _ from 'lodash';
import moment from 'moment';
import {
  BENCHMARKS_COLORS,
  calculatePercentage,
  isMulti
} from 'common/utils/utils';
import AppRouter from 'common/AppRouter';
import {
  metricDisplayValues,
  metricNames,
  METRICS_CALCULATION_STRATEGY_LABEL
} from '@linearb/linjs-common';
import CustomReportBarChart from './Graphs/CustomReportBarChart';
import CustomReportStackedAreaChart from './Graphs/CustomReportStackedAreaChart';
import CustomReportAreaChart from './Graphs/CustomReportAreaChart';
import { isDisplayBenchmark } from 'common/services/benchmarks.service';
import { isMedianMetric, getGranularityByDates } from './custom-report.util';

export const METRICS_GROUP_BY_OPTIONS = {
  // groupBy supported keys to be grouping the response data from backend into groups for multiple series
  CONTRIBUTOR: 'contributor_id',
  TEAM: 'team_id',
  REPOSITORY: 'repository_id',
  SERVICE: 'service_id',
  LABEL: 'label_id',
  ORGANIZATION: 'organization_id'
};

export const METRICS_GROUP_BY_API_PARAMS = {
  // groupBy supported params to be send to /search metrics api
  CONTRIBUTOR: 'contributor',
  TEAM: 'team',
  REPOSITORY: 'repository',
  SERVICE: 'service',
  ORGANIZATION: 'organization',
  CUSTOM_METRIC: 'custom_metric'
};

const groupEntityKeys = {
  [METRICS_GROUP_BY_OPTIONS.TEAM]: {
    color: 'color',
    name: 'name'
  }
};

export const getMetricsAvgForDisabledSeriesLegend = ({
  graph,
  metrics,
  disabledSeriesIdsByMetricName,
  isCustomPmMetrics
}) => {
  const metricName = _.get(graph, 'metrics[0].name');
  const disabledSeriesIds = _.get(
    disabledSeriesIdsByMetricName,
    `${metricName}`,
    []
  );

  return metrics
    .map(item =>
      (!disabledSeriesIds.includes(item.group) && !isCustomPmMetrics) ||
      graph.id === item.group
        ? item.data
        : []
    )
    .flat()
    .filter(Boolean);
};

export const checkIfIsPmCustomDashboard = ({ report }) =>
  _.get(report, 'internal_type', '') ===
  DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS;

export const getGranularityAccordingToDatesAndChoice = (
  start_date,
  end_date,
  selectedGranularity = GRANULARITY.AUTO,
  isMetricsV2,
  workingDaysSetting
) => {
  const granularity = getGranularityByDates({
    start_date,
    end_date,
    selectedGranularity
  });
  const rollupDateRanges = getTimeRangesByGranularity(
    start_date,
    end_date,
    granularity,
    isMetricsV2,
    workingDaysSetting
  );
  return { granularity, rollupDateRanges };
};

export const getYData = ({
  metrics = [],
  metricName,
  avgByName = null,
  percentOff = null,
  metricsCalculationStrategy
}) => {
  const metricNameWithV3 = isMedianMetric(metricName)
    ? `${metricName}:${metricsCalculationStrategy}`
    : metricName;
  return metrics.map(metric => {
    const dataPoint = metric[metricNameWithV3];
    if (percentOff) {
      const percentVal = _.get(metric, percentOff, 0);

      return {
        y: calculatePercentage(dataPoint || 0, percentVal, false),
        percentage: percentVal || 0,
        rawData: metric[metricNameWithV3]
      };
    }
    return dataPoint || 0;
  });
};

export const getXAxisLabel = (
  after,
  before,
  granularity = GRANULARITY.DAYS,
  name,
  workingDaysSetting = {}
) => {
  // This function return a formatted view of the point of granularity
  const granularityMap = {
    [GRANULARITY.DAYS]: moment(before).format(SHORT_DATE_FORMAT),
    [GRANULARITY.WEEKS]:
      WORKING_DAYS_WEEK_TYPE[workingDaysSetting?.value] === 'week'
        ? `Week ${moment(after).week()}`
        : `Week ${moment(after).isoWeek()}`,
    [GRANULARITY.MONTHS]: moment(before).format('MMM'),
    [GRANULARITY.ITERATIONS]: name
  };

  return granularityMap[granularity];
};

const getLabel = (metricsCalculationStrategy, label, metricName) => {
  label = isMedianMetric(metricName)
    ? `${METRICS_CALCULATION_STRATEGY_LABEL[metricsCalculationStrategy]} ${label}`
    : label;
  return label.toLowerCase();
};

export const labelBuilder = (
  label,
  percentValue = 0,
  percentName = null,
  overridePercentName = false,
  metricName = '',
  metricsCalculationConfig = {}
) => {
  const { metricsCalculationStrategy } = metricsCalculationConfig;
  label = getLabel(metricsCalculationStrategy, label, metricName);

  return percentName && !overridePercentName
    ? `${label} (out of ${percentValue} ${percentName.toLowerCase()})`
    : label;
};

const extractGroupEntityProps = (groupEntity, metricsGroupByKey) => {
  const keys = groupEntityKeys[metricsGroupByKey];

  const color = _.get(groupEntity, _.get(keys, 'color'));
  const name = _.get(groupEntity, _.get(keys, 'name'));

  return {
    name,
    color
  };
};

export const getMultiSeriesSharedPoints = graph => {
  const series = _.get(graph, 'series.chart.series');

  const points = [{ ...graph.point, selected: true }];
  _.forEach(series, s => {
    if (s !== graph.series && s.visible) {
      const point = s.points[graph.point.index];
      points.push(point);
    }
  });
  return points;
};

const getNewSeries = ({
  metrics,
  metricsGroupByKey,
  getMetricsData,
  getGroupEntity = _.noop,
  defaultColor = getColor('dodgerBlue'),
  graphId,
  isPmCustomDashboard
}) => {
  const groupMetrics = metrics
    .filter(metric => !isPmCustomDashboard || metric.group === graphId)
    .reduce((acc, curr) => {
      acc[curr.group] = curr.data;
      return acc;
    }, {});
  const isMultiSeries = isMulti(groupMetrics);

  return _.map(groupMetrics, (values, groupId) => {
    const groupEntity = getGroupEntity(metricsGroupByKey, groupId);
    const { name, color } = extractGroupEntityProps(
      groupEntity,
      metricsGroupByKey
    );

    return {
      name: isMultiSeries ? name : `organization`,
      data: getMetricsData(values),
      color: isMultiSeries ? color : defaultColor,
      type: isMultiSeries ? GRAPH_TYPES.SPLINE : GRAPH_TYPES.AREA_SPLINE,
      showInLegend: isMultiSeries,
      marker: {
        enabled: !isMultiSeries,
        symbol: 'circle',
        lineColor: null
      },
      customState: {
        groupId,
        metricsGroupByKey
      }
    };
  });
};

export const localStorageService = () => {
  const localStorageAccessor = BrowserStorageAccessor().local;

  const getNewTabsClicked = () => {
    return (
      JSON.parse(
        localStorageAccessor.get(
          LOCAL_STORAGE_KEYS.HIDE_NEW_TAG_PM_CUSTOM_DASHBOARD
        )
      ) || {}
    );
  };

  const setNewTabsClicked = ({ newTabsClicked }) => {
    localStorageAccessor.set(
      LOCAL_STORAGE_KEYS.HIDE_NEW_TAG_PM_CUSTOM_DASHBOARD,
      JSON.stringify(newTabsClicked)
    );
  };

  return {
    getNewTabsClicked,
    setNewTabsClicked
  };
};

export const getGraph = ({
  graphType,
  metricName,
  metrics,
  granularity = GRANULARITY.DAYS,
  avgByName = null,
  percentOff = null,
  graphColor,
  annotations,
  searchRange,
  unitType,
  displayName,
  displayLabel = displayName,
  onPointClick,
  axisTitle,
  unitDescription,
  graphId,
  metricsGroupByKey, // TODO: Oriel/Tomer - consider replacing metricsGroupByKey and getGroupEntity with metricGroupBy
  getGroupEntity,
  overridePercentName,
  selectedTimeRanges,
  widget = {},
  metricsCalculationConfig = {},
  isPmCustomDashboard = false,
  toggleDisabledSeriesIdsByMetricName,
  workingDaysSetting
}) => {
  const getEndDateForTooltip = xValue => {
    const { end_date } = selectedTimeRanges;
    return granularity === GRANULARITY.ITERATIONS
      ? moment(xValue)
      : moment.min(moment(end_date), moment(xValue));
  };
  const { metricsCalculationStrategy } = metricsCalculationConfig;

  const getNewMetricsData = metrics =>
    getYData({
      metrics,
      metricName,
      avgByName,
      percentOff: _.get(percentOff, 'metrics[0].name', null),
      metricsCalculationStrategy
    });
  const series = getNewSeries({
    metrics,
    metricsGroupByKey,
    getMetricsData: getNewMetricsData,
    getGroupEntity,
    defaultColor: getColor(graphColor),
    metricName,
    isPmCustomDashboard,
    graphId
  });

  const isMultiSeries = isMulti(series);

  const getCustomPmDashboard = () => {
    const relevantWidgetMetric = metrics.find(({ group }) => group === graphId);
    return relevantWidgetMetric?.data;
  };
  const percentOffName = _.get(percentOff, 'name', null);
  const yData = getYData({
    metrics: isPmCustomDashboard
      ? getCustomPmDashboard()
      : _.get(metrics, '[0].data', []),
    metricName,
    avgByName,
    percentOff: _.get(percentOff, 'metrics[0].name', null),
    customPmMetricWidgetId: _.get(widget, 'id'),
    graphId,
    metricsCalculationStrategy
  });

  const yFormatter = _.get(FORMATTERS, `${unitType}.yFormatter`, null);

  const isClickable = !!onPointClick;

  if (graphType === GRAPH_TYPES.BAR && !isMultiSeries) {
    return (
      <CustomReportBarChart
        searchRange={searchRange}
        yData={yData}
        onPointClick={onPointClick}
        displayName={displayName}
        yFormatter={yFormatter}
        granularity={granularity}
        graphColor={graphColor}
        displayLabel={displayLabel}
        percentOffName={percentOffName}
        overridePercentName={overridePercentName}
        metricName={metricName}
        isPmCustomDashboard={isPmCustomDashboard}
        getEndDateForTooltip={getEndDateForTooltip}
        metricsCalculationConfig={metricsCalculationConfig}
        workingDaysSetting={workingDaysSetting}
      />
    );
  } else if (graphType === GRAPH_TYPES.STACKED && !isMultiSeries) {
    return (
      <CustomReportStackedAreaChart
        searchRange={searchRange}
        granularity={granularity}
        metrics={metrics}
        graphId={graphId}
        annotations={annotations}
        axisTitle={axisTitle}
        unitDescription={unitDescription}
        isClickable={isClickable}
        getEndDateForTooltip={getEndDateForTooltip}
        workingDaysSetting={workingDaysSetting}
      />
    );
  }

  return (
    <CustomReportAreaChart
      searchRange={searchRange}
      granularity={granularity}
      series={series}
      isClickable={isClickable}
      getEndDateForTooltip={getEndDateForTooltip}
      yFormatter={yFormatter}
      isMultiSeries={isMultiSeries}
      displayLabel={displayLabel}
      percentOffName={percentOffName}
      metricName={metricName}
      metricsCalculationConfig={metricsCalculationConfig}
      onPointClick={onPointClick}
      graphColor={graphColor}
      toggleDisabledSeriesIdsByMetricName={toggleDisabledSeriesIdsByMetricName}
      workingDaysSetting={workingDaysSetting}
    />
  );
};

export const GetMetricsMetadata = (
  graphId,
  defaultTeam,
  getSettingOrDefinitionValue
) => {
  const graphData = METRICS_METADATA[graphId];
  if (!graphData) return {};

  const {
    settingName,
    description,
    axisTitle,
    goalsValues,
    didNotMeetTooltipDescription = _.noop
  } = graphData;
  const teamId = _.get(defaultTeam, 'id');
  const settingValue =
    getSettingOrDefinitionValue({
      name: settingName,
      definitionLevel: 'team',
      teamId
    }) || {};

  const subtitle = description(settingValue);
  const didNotMeetGoalTooltipDescription =
    didNotMeetTooltipDescription(settingValue);

  return {
    subtitle,
    didNotMeetGoalTooltipDescription,
    goalsValues,
    axisTitle
  };
};

export const getCalculatedValue = (
  metricName,
  avgByMetric = null,
  avgOnlyWhenDataAvailable = null,
  percentOff = null,
  metrics = []
) => {
  const metricsLength = metrics ? metrics.length : 0;
  if (!metricsLength) {
    return '-';
  }
  let avgByAmount;
  if (avgByMetric) {
    avgByAmount =
      metrics.reduce((acc, metric) => acc + (metric[avgByMetric] || 0), 0) ||
      metricsLength;
  } else if (avgOnlyWhenDataAvailable) {
    avgByAmount =
      _.filter(metrics, metric => _.isNumber(metric[metricName])).length || 1;
  } else {
    avgByAmount = metricsLength;
  }
  const metricToAvg = metrics.reduce(
    (acc, metric) => acc + _.get(metric, metricName, 0),
    0
  );
  const nonPercentValue = metricToAvg / avgByAmount;
  const value = percentOff
    ? calculatePercentage(nonPercentValue, percentOff, false)
    : nonPercentValue;
  return value > 0 ? value : '-';
};

export const calculateReportHeight = (graphs, isEditable, userCanAddGraphs) => {
  const isFull =
    (userCanAddGraphs ? graphs.length : graphs.length - 1) % 3 === 0 &&
    isEditable
      ? 0
      : 1;
  const addGap = isEditable ? 0 : 33;
  const rows = Math.ceil(graphs.length / 3);
  const deviation = rows - 1.9 + !isFull;
  const reportHeightWhenEdit = (rows - isFull) * 35 + addGap - deviation;
  const reportHeight =
    userCanAddGraphs && isEditable ? reportHeightWhenEdit : rows * 35;
  return reportHeight === 0 ? '0.03' : reportHeight;
};

export const calculateAddButtonPosition = graphs => {
  return {
    left: `${(graphs.length % 3) * 33.3}%`,
    bottom: `${
      graphs.length % 3 === 0
        ? graphs.length / 3 <= 1
          ? '1'
          : '3'
        : (Math.ceil(graphs.length / 3) - 1) * 1.4
    }rem`
  };
};

export const DEFAULT_GRAPH_EDIT_CONFIG = {
  canChangeType: true,
  canChangeColor: true,
  canDelete: true
};

export const getGraphEditConfig = ({ type, teams, labels }) => ({
  // Interface: {type: bool, color: bool, delete: bool}
  ...DEFAULT_GRAPH_EDIT_CONFIG,
  ...(((type === FILTER_BY.PEOPLE && isMulti(teams)) ||
    (type === FILTER_BY.LABEL && labels.length > 0)) && {
    canChangeType: false,
    canChangeColor: false
  })
});

export function metricMaxPoint(metrics, metricDataKey) {
  return _.max(metrics.map(item => item[metricDataKey])) || 0;
}

export function mapMetricsToStackBarSeries(
  metrics,
  violationMetric,
  totalMetric
) {
  const metricsOrderedBeBeforeAsc = _.orderBy(
    metrics,
    metric => moment(metric.before).format('YYYYMMDD'),
    ['asc']
  );

  return [
    {
      name: METRICS_GOALS.DID_NOT_MEET_GOALS.text,
      data: metricsOrderedBeBeforeAsc.map(
        metric => metric[violationMetric] || 0
      ),
      color: METRICS_GOALS.DID_NOT_MEET_GOALS.color,
      dateRange: metricsOrderedBeBeforeAsc.map(({ after, before }) => {
        return {
          after,
          before
        };
      })
    },
    {
      name: METRICS_GOALS.MET_GOALS.text,
      data: metricsOrderedBeBeforeAsc.map(metric => {
        const prsMerged = metric[totalMetric] || 0;
        const didNotMeetGoal = metric[violationMetric] || 0;
        const prsMetGoal = prsMerged - didNotMeetGoal;
        return preventNegativeValue(prsMetGoal);
      }),
      color: METRICS_GOALS.MET_GOALS.color,
      dateRange: metricsOrderedBeBeforeAsc.map(({ after, before }) => {
        return {
          after,
          before
        };
      })
    }
  ];
}

export const calcMetricMetGoalsPercent = (
  metrics,
  violationMetric,
  totalMetric
) => {
  const prsMergedSum = _.sum(metrics.map(metric => metric[totalMetric] || 0));
  const didNotMeetSum = _.sum(
    metrics.map(metric => metric[violationMetric] || 0)
  );
  const prsMetGoalSum = prsMergedSum - didNotMeetSum;

  if (prsMergedSum === 0) {
    return { percent: 0, prsMergedSum, didNotMeetSum };
  }

  const prsMetGoalPercent = calculatePercentage(prsMetGoalSum, prsMergedSum);

  if (
    isNaN(prsMergedSum) ||
    isNaN(didNotMeetSum) ||
    isNaN(prsMetGoalPercent) ||
    prsMetGoalSum < 0
  ) {
    return { percent: 0, prsMergedSum, didNotMeetSum };
  }

  return { percent: prsMetGoalPercent, prsMergedSum, didNotMeetSum };
};

const preventNegativeValue = value => {
  return value < 0 ? 0 : value;
};

export const GRAPH_REDIRECT = {
  teamGoals: {
    goTo: (
      history,
      pmSensor,
      selectTeamId,
      globallySelectedTeamId,
      graphId = null
    ) => {
      selectTeamId(globallySelectedTeamId);
      history.push({
        pathname: `${AppRouter.SETTINGS.path}/notifications`,
        state: { highlightId: graphId }
      });
    },
    tooltip: 'Go to settings to customize your team goals',
    showGoToSettings: true
  },
  MTTR: {
    goTo: (history, pmSensor) =>
      history.push({
        pathname: `${
          AppRouter.PROJECT_MANAGEMENT.path
        }/${pmSensor.sensor_name.toLowerCase()}/incidents`,
        search: 'isOrgSettings=true'
      }),
    tooltip:
      'Go to settings to customize the way production bugs are identified',
    dataTestid: 'mttr-setting',
    isUsingPmSensor: true
  },
  CFR: {
    goTo: (history, pmSensor) =>
      history.push({
        pathname: `${
          AppRouter.PROJECT_MANAGEMENT.path
        }/${pmSensor.sensor_name.toLowerCase()}/incidents`,
        search: 'isOrgSettings=true'
      }),
    tooltip:
      'Go to settings to customize the way production bugs are identified',
    dataTestid: 'cfr-setting',
    isUsingPmSensor: true
  }
};

/**
 * @typedef {object} Params
 * @property {any} point onclick, highcharts calls a function with point
 * @property {any} pmSensor pm sensor configured for the org, needed for jira url
 * @property {any} metrics needed in order to get relevant issues for the current point
 */

export const onLegendSeriesClick = (
  event,
  metricName,
  toggleDisabledSeriesIdsByMetricName
) => {
  const clickedSeries = _.get(event, 'target');
  const groupId = _.get(clickedSeries, 'userOptions.customState.groupId');
  toggleDisabledSeriesIdsByMetricName(metricName, groupId);
};

const SHOW_GIT_STREAM_CTA_BENCHMARKS_NAMES = [
  metricNames.TIME_TO_REVIEW,
  metricNames.REVIEW_TIME
];

const SHOW_GIT_STREAM_CTA_CUSTOM_REPORT_CATEGORY = 'delivery';

const SHOW_GIT_STREAM_CTA_BENCHMARKS_COLORS = [
  BENCHMARKS_COLORS.FAIR,
  BENCHMARKS_COLORS.POOR
];

// returns a boolean value. checks if should show GitStreamCTA badge - will show only if there is benchmark icon
// will show only if benchmark color is fair or poor and only in the report Delivery.
export const shouldShowGitStreamCTA = (
  value,
  benchmarkRange,
  graph,
  getSettingOrDefinitionValue,
  selectedTeamId
) => {
  const isThereBenchmark =
    benchmarkRange &&
    _.isNumber(value) &&
    isDisplayBenchmark({ getSettingOrDefinitionValue, selectedTeamId });

  if (isThereBenchmark) {
    const { icon_color } = benchmarkRange;
    const isShowGitStreamCTACategory =
      _.get(graph, 'category', '').toLowerCase() ===
      SHOW_GIT_STREAM_CTA_CUSTOM_REPORT_CATEGORY;

    const graphName = _.get(graph, 'name').toLowerCase();
    const isShowGitStreamCTABenchmark =
      !!SHOW_GIT_STREAM_CTA_BENCHMARKS_NAMES.find(
        benchmarkName =>
          metricDisplayValues[benchmarkName].displayName.toLowerCase() ===
          graphName
      );

    return (
      isShowGitStreamCTACategory &&
      isShowGitStreamCTABenchmark &&
      SHOW_GIT_STREAM_CTA_BENCHMARKS_COLORS.includes(icon_color)
    );
  }

  return false;
};
